import { withGst } from '@/shared/lib/currency';
import { DATE_FORMAT, dayjs } from '@/shared/lib/dayjs';

import {
  DOCUMENT_TYPE,
  ISSUED_BY_ASIC,
  PAY_STATUS,
  SIGN_STATUS,
  type AttachedInvoice,
  type OutstandingItem,
} from './outstanding.schema';

/** Rows the director owes money on, in any of the states that mean "not settled". */
export const isUnpaid = (item: OutstandingItem): boolean =>
  item.payStatus === PAY_STATUS.unpaid || item.payStatus === PAY_STATUS.failed;

/** True only when this director still has to act. Once they have signed, a row
 *  waiting on co-directors is no longer their task. */
export const needsMySignature = (item: OutstandingItem): boolean =>
  item.signStatus === SIGN_STATUS.unsigned && !item.hasSigned;

/**
 * Whether a row carries a payment at all.
 *
 * The legacy version asked whether `name` was 'ASIC Invoice' or contained
 * 'Annual Review'. Both are display strings. A row is payable when it is an
 * invoice or has invoices attached — that is a structural fact.
 */
export const isPayable = (item: OutstandingItem): boolean =>
  item.documentType === DOCUMENT_TYPE.invoice || item.invoices.length > 0;

export const asicInvoiceOf = (item: OutstandingItem): AttachedInvoice | undefined =>
  item.invoices.find((invoice) => invoice.issuedBy === ISSUED_BY_ASIC);

export const agentInvoiceOf = (item: OutstandingItem): AttachedInvoice | undefined =>
  item.invoices.find((invoice) => invoice.issuedBy !== ISSUED_BY_ASIC);

/** What the director actually owes on a row, including GST on the agent's leg. */
export const amountDueOf = (item: OutstandingItem): number => {
  if (!isUnpaid(item)) return 0;

  if (item.annualReviewId !== '') {
    const agent = item.agentInvoiceStatus === PAY_STATUS.paid ? 0 : withGst(item.agentAmount);
    const asic = item.asicInvoiceStatus === PAY_STATUS.paid ? 0 : item.asicAmount;
    return agent + asic;
  }

  const attached = item.invoices[0];
  if (attached !== undefined) return attached.amount;

  return item.asicAmount;
};

/**
 * The signable document id.
 *
 * An annual review is signed by signing its solvency resolution, not the row
 * itself. Getting this wrong signs the wrong document.
 */
export const signableDocumentIdOf = (item: OutstandingItem): string =>
  item.solvency?.id !== undefined && item.solvency.id !== '' ? item.solvency.id : item.id;

/** A row is overdue once its due date is behind us. The legacy comparison
 *  (`unix() < now - 1 day`) gave every row a silent extra day of grace, which
 *  meant the table disagreed with the reminder emails. */
export const isOverdue = (item: OutstandingItem): boolean => {
  if (item.dueDate === null || item.dueDate === undefined || item.dueDate === '') return false;
  const due = dayjs(item.dueDate);
  return due.isValid() && due.endOf('day').isBefore(dayjs());
};

/**
 * The deadline, in the terms a director cares about.
 *
 * "11 days late" lands where "due 14 Sep" does not — the reader should not have
 * to subtract today's date from a printed one to learn they have a problem.
 *
 * Shared rather than local to the card because the pay step states the same
 * fact, and two independent renderings of "how late is this" is how the list and
 * the step end up disagreeing about a document.
 */
export const deadlineOf = (
  item: OutstandingItem,
): { text: string; late: boolean } | undefined => {
  const raw = item.dueDate;
  if (raw === null || raw === undefined || raw === '') return undefined;

  const due = dayjs(raw);
  if (!due.isValid()) return undefined;

  const formatted = due.format(DATE_FORMAT.date);
  if (!isOverdue(item)) return { text: `Due ${formatted}`, late: false };

  const days = dayjs().startOf('day').diff(due.startOf('day'), 'day');
  const lateness = days === 1 ? '1 day late' : `${String(days)} days late`;
  return { text: `${lateness} · was due ${formatted}`, late: true };
};

/**
 * True when this row is settled from an SMSF bank account.
 *
 * The ATO forbids paying an SMSF liability by credit card, so when the agent
 * has `disableSmsfCC` set the card methods are withdrawn entirely rather than
 * shown and rejected later.
 */
export const isSuperFund = (item: OutstandingItem): boolean =>
  item.invoices.some((invoice) => invoice.superFund);

/** Display name. Derived last, so nothing branches on it. */
export const titleOf = (item: OutstandingItem): string => {
  if (item.documentType === DOCUMENT_TYPE.dividend) return 'Dividend Statement';
  if (item.documentType === DOCUMENT_TYPE.form && item.type !== '') {
    return item.name === '' ? `Form ${item.type}` : `Form ${item.type} — ${item.name}`;
  }
  return item.name;
};

/**
 * Collapses duplicate unpaid ASIC invoices to the most recently issued one.
 *
 * Ported from the legacy screen, with two corrections. It grouped by
 * `asicCompanyName`, so two companies registered under the same name collapsed
 * into one row — this keys on `asicCompanyId`. And it compared `dayjs().unix()`
 * on possibly-absent dates, where `NaN < NaN` is false and the first row won by
 * accident; an absent date now loses to a present one deterministically.
 *
 * This belongs in the query, not the client — it runs after pagination, so a
 * collapsed row still consumed a slot and pages render short. Flagged for the
 * backend; kept here meanwhile so behaviour does not change.
 */
export const dedupeAsicInvoices = (items: OutstandingItem[]): OutstandingItem[] => {
  const latestByCompany = new Map<string, OutstandingItem>();
  const passthrough: OutstandingItem[] = [];

  const issuedAt = (item: OutstandingItem): number => {
    const raw = item.invoices[0]?.dateIssued;
    if (raw === undefined || raw === '') return -Infinity;
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.valueOf() : -Infinity;
  };

  for (const item of items) {
    const isDuplicableAsicInvoice =
      item.documentType === DOCUMENT_TYPE.invoice &&
      item.annualReviewId === '' &&
      item.payStatus === PAY_STATUS.unpaid;

    if (!isDuplicableAsicInvoice) {
      passthrough.push(item);
      continue;
    }

    const held = latestByCompany.get(item.asicCompanyId);
    if (held === undefined || issuedAt(item) > issuedAt(held)) {
      latestByCompany.set(item.asicCompanyId, item);
    }
  }

  return [...passthrough, ...latestByCompany.values()];
};

/**
 * Urgency order: overdue first, then due soonest, then rows with no due date.
 *
 * The legacy list arrived in whatever order the API returned and left the
 * director to sort a column to discover what was late. Sort order is a product
 * decision, so it is made here rather than deferred to the user.
 */
export const byUrgency = (a: OutstandingItem, b: OutstandingItem): number => {
  const dueValue = (item: OutstandingItem): number => {
    if (item.dueDate === null || item.dueDate === undefined || item.dueDate === '') {
      return Infinity;
    }
    const parsed = dayjs(item.dueDate);
    return parsed.isValid() ? parsed.valueOf() : Infinity;
  };

  const difference = dueValue(a) - dueValue(b);
  if (difference !== 0 && Number.isFinite(difference)) return difference;
  // Both undated, or the same date: company name keeps the order stable so the
  // list does not reshuffle between renders.
  return a.asicCompanyName.localeCompare(b.asicCompanyName);
};
